import { FunctionComponent } from "react";
import { FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { managementToolbarSelectSx } from "../utils/management-toolbar.styles";
import { formatManagementLocationDisplayName } from "../utils/management-capa-chart.utils";

export type ManagementCompareLocationOption = {
  id: number;
  name: string;
};

export type ManagementCompareFloorOption = {
  id: number;
  name: string;
};

export type ManagementCompareSelectsProps = {
  locations: ManagementCompareLocationOption[];
  floors: ManagementCompareFloorOption[];
  locationId: string;
  floorId: number | null;
  /** Primary floor already in use — disabled in the compare floor list. */
  excludedFloorId?: number | null;
  locationsLoading?: boolean;
  floorsLoading?: boolean;
  onLocationChange: (locationId: string) => void;
  onFloorChange: (floorId: number | null) => void;
  locationLabelId?: string;
};

/**
 * Shared location + floor selects for Management floor comparison.
 */
export const ManagementCompareSelects: FunctionComponent<ManagementCompareSelectsProps> = ({
  locations,
  floors,
  locationId,
  floorId,
  excludedFloorId = null,
  locationsLoading = false,
  floorsLoading = false,
  onLocationChange,
  onFloorChange,
  locationLabelId
}) => {
  const { t } = useTranslation();

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
      <FormControl fullWidth size="small" sx={managementToolbarSelectSx}>
        <Select
          labelId={locationLabelId}
          value={locationId}
          displayEmpty
          onChange={event => onLocationChange(String(event.target.value))}
          data-testid="management-compare-location-select"
          disabled={locationsLoading}
          inputProps={{ "aria-label": t("_management_compareSelectLocation") }}
        >
          <MenuItem value="">
            <Typography component="span" color="text.secondary">
              {t("_management_compareSelectLocation")}
            </Typography>
          </MenuItem>
          {locations.map(location => (
            <MenuItem key={location.id} value={String(location.id)}>
              {formatManagementLocationDisplayName(location.name, t)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth size="small" sx={managementToolbarSelectSx}>
        <Select
          value={floorId ?? ""}
          displayEmpty
          onChange={event => {
            const next = event.target.value;
            onFloorChange(next === "" ? null : Number(next));
          }}
          data-testid="management-compare-floor-select"
          disabled={floorsLoading || locationId === "" || floors.length === 0}
          inputProps={{ "aria-label": t("_management_compareSelectFloor") }}
        >
          <MenuItem value="">
            <Typography component="span" color="text.secondary">
              {t("_management_compareSelectFloor")}
            </Typography>
          </MenuItem>
          {floors.map(floor => (
            <MenuItem
              key={floor.id}
              value={floor.id}
              disabled={excludedFloorId != null && floor.id === excludedFloorId}
            >
              {floor.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};
